import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

/**
 * Carte « Dictée vocale (Whisper) » du Super Admin (Paramètres → API & IA).
 * Active/désactive la saisie vocale dans l'espace client et permet d'éditer
 * l'URL de l'API Whisper (serveur Docker) et le modèle utilisé.
 *
 * La clé API Whisper (WHISPER_API_KEY) est stockée comme secret backend et
 * n'est jamais exposée dans cette interface ni dans le navigateur.
 */

const LANGUAGES = [
  { code: 'auto', label: 'Détection automatique' },
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'Anglais' },
  { code: 'ar', label: 'Arabe' },
  { code: 'darija', label: 'Darija marocain' },
  { code: 'es', label: 'Espagnol' },
];

export default function WhisperSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [model, setModel] = useState('');
  const [language, setLanguage] = useState('auto');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; latencyMs?: number } | null>(null);
  const [usage, setUsage] = useState<{ total: number; today: number; week: number; month: number } | null>(null);

  const call = useCallback(async (payload: Record<string, unknown>) => {
    const base = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const anon = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;
    const resp = await fetch(`${base}/functions/v1/transcribe-audio`, {
      method: 'POST',
      headers: { apikey: anon, Authorization: `Bearer ${anon}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    let data: Record<string, unknown> = {};
    try { data = (await resp.json()) as Record<string, unknown>; } catch { data = {}; }
    if (!resp.ok) throw new Error(String(data?.message || 'Erreur'));
    return data;
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    call({ action: 'admin_config', userId: user.id })
      .then((data) => {
        if (cancelled) return;
        setEnabled(data?.enabled === true);
        setApiUrl(String(data?.apiUrl || ''));
        setModel(String(data?.model || ''));
        setLanguage(String(data?.language || 'auto'));
      })
      .catch(() => { /* garde les valeurs par défaut */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id, call]);

  const save = async () => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    if (enabled && !apiUrl.trim()) {
      setError("Une URL d'API est requise pour activer la dictée vocale.");
      setSaving(false);
      return;
    }
    try {
      await call({
        action: 'save_config',
        userId: user.id,
        enabled,
        apiUrl: apiUrl.trim(),
        model: model.trim(),
        language,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const loadUsage = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await call({ action: 'usage_stats', userId: user.id });
      setUsage({
        total: Number(data?.total) || 0,
        today: Number(data?.today) || 0,
        week: Number(data?.week) || 0,
        month: Number(data?.month) || 0,
      });
    } catch { /* suivi indisponible : on n'affiche rien */ }
  }, [user?.id, call]);

  useEffect(() => { void loadUsage(); }, [loadUsage]);

  const testConnection = async () => {
    if (!user?.id) return;
    setTesting(true);
    setTestResult(null);
    try {
      const data = await call({ action: 'test_connection', userId: user.id });
      setTestResult({
        ok: data?.ok === true,
        message: String(data?.message || (data?.ok ? 'Connexion réussie.' : 'Échec du test.')),
        latencyMs: typeof data?.latencyMs === 'number' ? data.latencyMs : undefined,
      });
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof Error ? e.message : 'Test impossible.' });
    } finally {
      setTesting(false);
    }
  };

  const statusLabel = loading ? '…' : enabled ? 'Activé' : 'Désactivé';

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-mic-line text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-foreground-900">Dictée vocale (Whisper)</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              enabled ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {statusLabel}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-4">
            Permet aux utilisateurs de dicter leur message au lieu de le taper. L'audio est transcrit par
            votre serveur Whisper (Docker) via une fonction backend sécurisée. La clé API reste côté serveur.
          </p>

          {/* Activer / Désactiver */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-background-100 mb-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground-800">Activer la dictée vocale</p>
              <p className="text-[11px] text-foreground-500">Affiche le bouton microphone dans le chat IA des clients.</p>
            </div>
            <button
              type="button"
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                enabled ? 'bg-accent-500' : 'bg-background-200'
              }`}
              aria-label={enabled ? 'Désactiver' : 'Activer'}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Indicateur « Dictée active » : combien de messages sont dictés plutôt que tapés. */}
          {usage && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 p-3 rounded-lg bg-accent-50/60 border border-accent-100 mb-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-foreground-800 whitespace-nowrap">
                <span className="w-6 h-6 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className="ri-mic-line"></i>
                </span>
                Dictée active
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-600">
                <span><strong className="text-foreground-900">{usage.total}</strong> messages dictés (total)</span>
                <span><strong className="text-foreground-900">{usage.month}</strong> sur 30 j</span>
                <span><strong className="text-foreground-900">{usage.week}</strong> sur 7 j</span>
                <span><strong className="text-foreground-900">{usage.today}</strong> aujourd&apos;hui</span>
              </div>
            </div>
          )}

          {/* Champs */}
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">
                URL de l'API (endpoint de transcription)
              </label>
              <input
                type="text"
                value={apiUrl}
                onChange={(e) => setApiUrl(e.target.value)}
                placeholder="https://whisper-api.votre-domaine/v1/audio/transcriptions"
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Modèle</label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Systran/faster-whisper-small"
                  className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Langue par défaut</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Test de connexion au serveur Whisper (réservé au Super Admin) */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button
              type="button"
              onClick={testConnection}
              disabled={testing || loading}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer border border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors disabled:opacity-50"
            >
              {testing ? (
                <><i className="ri-loader-4-line animate-spin"></i>Test en cours…</>
              ) : (
                <><i className="ri-plug-line"></i>Tester la connexion Whisper</>
              )}
            </button>
          </div>
          {testResult && (
            <div className={`mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
              <i className={`${testResult.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mt-0.5 flex-shrink-0`}></i>
              <span>
                {testResult.message}
                {typeof testResult.latencyMs === 'number' && (
                  <span className="text-foreground-400"> · {testResult.latencyMs} ms</span>
                )}
              </span>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>{error}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
            <p className="text-[10px] text-foreground-400 flex items-center gap-1.5 max-w-md">
              <i className="ri-shield-check-line text-accent-500"></i>
              La clé <span className="font-mono">WHISPER_API_KEY</span> est stockée en secret backend et n&apos;est jamais exposée au navigateur.
            </p>
            <button
              onClick={save}
              disabled={saving || loading}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</>
              ) : saved ? (
                <><i className="ri-check-line"></i>Enregistré</>
              ) : (
                <><i className="ri-save-line"></i>Enregistrer</>
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}